import { useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { Upload, X, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function CustomOrder() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: "",
    product_type: "resin-art",
    description: "",
    reference_images: [],
    budget_min: "",
    budget_max: "",
    expected_delivery: "",
    occasion: "",
  });

  const uploadImages = async (files) => {
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of Array.from(files)) {
        if (file.size > 8 * 1024 * 1024) { toast.error(`${file.name} is too large (max 8MB)`); continue; }
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(`${BACKEND}${data.url}`);
      }
      setForm((f) => ({ ...f, reference_images: [...f.reference_images, ...uploaded] }));
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const removeImage = (url) => setForm((f) => ({ ...f, reference_images: f.reference_images.filter((u) => u !== url) }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        budget_min: form.budget_min ? Number(form.budget_min) : null,
        budget_max: form.budget_max ? Number(form.budget_max) : null,
      };
      const { data } = await api.post("/custom-orders", payload);
      toast.success("Request received. We'll be in touch shortly.");
      if (user) nav("/account?tab=custom");
      else nav("/", { replace: true });
    } catch (e) { toast.error(e.response?.data?.detail || "Please try again"); } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="custom-order-page">
      <section className="relative min-h-[50vh] flex items-end overflow-hidden">
        <img src="https://images.unsplash.com/photo-1615529182904-14819c35db37" alt="Studio" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/60 via-charcoal/30 to-cream" />
        <div className="relative max-w-[1200px] mx-auto px-6 lg:px-12 pb-16">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
            <div className="overline text-ivory/85 mb-3 flex items-center gap-3"><Sparkles className="w-3 h-3 text-gold" /> Made-to-order</div>
            <h1 className="font-serif text-ivory text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02]">Commission a piece,<br /><em className="not-italic text-gold">shaped for you.</em></h1>
            <p className="mt-6 text-ivory/85 max-w-xl text-[16px] leading-relaxed">Tell us what you'd like — a colour that feels like the room, an initial for someone loved, a wave from a place you remember. We'll come back with a quote within 48 hours.</p>
          </motion.div>
        </div>
      </section>

      <section className="max-w-[1000px] mx-auto px-6 lg:px-12 py-20 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-1">
          <div className="overline text-emerald mb-3">How it works</div>
          <ol className="space-y-6 text-sm text-charcoal/80">
            {["Send us your brief with references, budget, and any dates you have in mind.", "We reply within 48 hours with a quote and a delivery estimate.", "Approve the quote, pay by card or Cash on Delivery, and we begin.", "You'll see studio progress photos throughout — and the final piece before we ship."].map((t, i) => (
              <li key={i} className="flex gap-4"><span className="font-serif text-2xl text-gold leading-none">0{i + 1}</span><span className="flex-1 leading-relaxed pt-1">{t}</span></li>
            ))}
          </ol>
        </div>

        <form onSubmit={submit} className="lg:col-span-2 bg-ivory/60 p-8 space-y-5" data-testid="custom-order-form">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="overline text-charcoal/60 mb-2 block">Your name</label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="co-name" className="bg-cream border-0 border-b divider-gold rounded-none focus-visible:ring-0" />
            </div>
            <div><label className="overline text-charcoal/60 mb-2 block">Email</label>
              <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="co-email" className="bg-cream border-0 border-b divider-gold rounded-none focus-visible:ring-0" />
            </div>
            <div><label className="overline text-charcoal/60 mb-2 block">Phone (optional)</label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="co-phone" className="bg-cream border-0 border-b divider-gold rounded-none focus-visible:ring-0" />
            </div>
            <div><label className="overline text-charcoal/60 mb-2 block">Product type</label>
              <Select value={form.product_type} onValueChange={(v) => setForm({ ...form, product_type: v })}>
                <SelectTrigger data-testid="co-type" className="bg-cream border-0 border-b divider-gold rounded-none focus:ring-0"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="resin-art">Resin Art</SelectItem>
                  <SelectItem value="handmade-bags">Handmade Bag</SelectItem>
                  <SelectItem value="pearl-jewellery">Pearl Jewellery</SelectItem>
                  <SelectItem value="other">Something else</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div><label className="overline text-charcoal/60 mb-2 block">Describe your vision</label>
            <Textarea required rows={6} placeholder="Tell us about colour, size, engraving/initials, occasion, materials, references…" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="co-desc" className="bg-cream border divider-gold rounded-none focus-visible:ring-0" />
          </div>

          <div>
            <label className="overline text-charcoal/60 mb-2 block">Reference images (optional)</label>
            <div className="border border-dashed divider-gold p-6 text-center">
              <input id="co-files" data-testid="co-files" type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadImages(e.target.files)} />
              <label htmlFor="co-files" className="cursor-pointer inline-flex items-center gap-2 overline text-emerald">
                <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Choose photos"}
              </label>
              <div className="text-[11px] text-charcoal/50 mt-2">Up to 8MB each · JPG / PNG</div>
            </div>
            {form.reference_images.length > 0 && (
              <div className="mt-4 grid grid-cols-4 gap-3">
                {form.reference_images.map((u) => (
                  <div key={u} className="relative">
                    <img src={u} alt="" className="w-full aspect-square object-cover bg-ivory" />
                    <button type="button" onClick={() => removeImage(u)} className="absolute top-1 right-1 p-1 bg-cream/95"><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div><label className="overline text-charcoal/60 mb-2 block">Budget · min ₹</label>
              <Input type="number" value={form.budget_min} onChange={(e) => setForm({ ...form, budget_min: e.target.value })} data-testid="co-budget-min" className="bg-cream border-0 border-b divider-gold rounded-none focus-visible:ring-0" />
            </div>
            <div><label className="overline text-charcoal/60 mb-2 block">Budget · max ₹</label>
              <Input type="number" value={form.budget_max} onChange={(e) => setForm({ ...form, budget_max: e.target.value })} data-testid="co-budget-max" className="bg-cream border-0 border-b divider-gold rounded-none focus-visible:ring-0" />
            </div>
            <div><label className="overline text-charcoal/60 mb-2 block">By when?</label>
              <Input type="date" value={form.expected_delivery} onChange={(e) => setForm({ ...form, expected_delivery: e.target.value })} data-testid="co-delivery" className="bg-cream border-0 border-b divider-gold rounded-none focus-visible:ring-0" />
            </div>
          </div>

          <div><label className="overline text-charcoal/60 mb-2 block">Occasion (optional)</label>
            <Input placeholder="Wedding, anniversary, housewarming…" value={form.occasion} onChange={(e) => setForm({ ...form, occasion: e.target.value })} className="bg-cream border-0 border-b divider-gold rounded-none focus-visible:ring-0" />
          </div>

          <Button type="submit" data-testid="co-submit" disabled={submitting} className="w-full rounded-none bg-emerald hover:bg-emerald/90 text-ivory h-12 tracking-[0.15em] uppercase text-xs">
            {submitting ? "Sending…" : "Send commission brief"}
          </Button>

          {!user && <p className="text-[11px] text-center text-charcoal/50">Tip: <Link to="/auth" className="text-emerald">create an account</Link> to track your quote and approve it later.</p>}
        </form>
      </section>
    </div>
  );
}
